/** Canonical public app URL for identity share/QR links. */
export function getPublicAppOrigin(): string {
  const fromEnv = import.meta.env.VITE_PUBLIC_APP_URL as string | undefined;
  if (fromEnv?.trim()) return fromEnv.replace(/\/+$/, '');
  if (typeof window !== 'undefined') return window.location.origin;
  return '';
}

export function buildIdentityUrl(code: string): string {
  return `${getPublicAppOrigin()}/g/${code.toUpperCase()}`;
}

/** Absolute guide detail page URL (`/guides/<profileId>`) — what the card's QR opens. */
export function buildGuideProfileUrl(profileId: string): string {
  return `${getPublicAppOrigin()}/guides/${profileId}`;
}

export async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through
  }

  try {
    const el = document.createElement('textarea');
    el.value = text;
    el.setAttribute('readonly', '');
    el.style.position = 'absolute';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(el);
    return ok;
  } catch {
    return false;
  }
}

export type ShareOutcome = 'shared' | 'copied' | 'cancelled' | 'failed';

export async function shareIdentity(payload: {
  title: string;
  text: string;
  url: string;
}): Promise<ShareOutcome> {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      await navigator.share(payload);
      return 'shared';
    }
  } catch (error) {
    // User dismissed the share sheet — not an error worth reporting.
    if ((error as { name?: string } | null)?.name === 'AbortError') return 'cancelled';
    // Anything else (e.g. NotAllowedError) falls back to copying the link.
  }

  const copied = await copyTextToClipboard(payload.url);
  return copied ? 'copied' : 'failed';
}
